import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LightningIcon } from '@phosphor-icons/react';
import type { AgentCatalogEntry } from '@/api/ai-service';
import { agentsOptions } from '@/hooks/query-options';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { ActionCard } from '@/components/actions/action-card';
import { agentVisual } from '@/components/actions/action-visuals';

export const Route = createFileRoute(
  '/_auth/_side-layout/super-admin/data/agents',
)({
  component: SuperAdminAgentsPage,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
});

// The agent's engine details as a quiet footer line: the memory it injects and
// its model — divider-separated, same flat treatment as the catalog card's
// run-mode line. Shown only when there's signal: the model is omitted when null
// (auto-routed — the default for most agents), and the line is dropped entirely
// when an agent has neither. Informational, so border/text only, never a pill.
function AgentTraits({ agent }: { agent: AgentCatalogEntry }) {
  const traits = [
    agent.context_kind ? `${agent.context_kind} memory` : null,
    agent.model, // null = auto-routed; surfaced only when explicitly pinned
  ].filter((trait): trait is string => Boolean(trait));
  if (traits.length === 0) return null;
  return (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
      {traits.map((trait, i) => (
        <span key={trait} className="flex items-center gap-2.5">
          {i > 0 && <span className="bg-border h-3 w-px" />}
          {trait}
        </span>
      ))}
    </div>
  );
}

// The billable actions this agent powers, as a quiet "Powers · A, B" line —
// mirrors <ActionAgentsMeta>'s inverse ("Agent · Name") on the catalog card.
function AgentPowers({ actions }: { actions: AgentCatalogEntry['actions'] }) {
  if (actions.length === 0) {
    return (
      <span className="text-muted-foreground text-sm">No actions yet</span>
    );
  }
  return (
    <div className="text-muted-foreground flex items-start gap-1.5 text-sm">
      <LightningIcon className="mt-0.5 size-3.5 shrink-0" />
      <span>
        Powers ·{' '}
        <span className="text-foreground">
          {actions.map((action) => action.label).join(', ')}
        </span>
      </span>
    </div>
  );
}

function AgentCard({ agent }: { agent: AgentCatalogEntry }) {
  const { Icon, accent } = agentVisual(agent.key);
  return (
    <ActionCard
      accent={accent}
      icon={<Icon />}
      title={agent.label}
      description={
        <div className="space-y-3">
          {agent.description && <p>{agent.description}</p>}
          {agent.guardrails.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-foreground text-xs font-medium">Guardrails</p>
              <ul className="list-disc space-y-1 pl-4">
                {agent.guardrails.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      }
      footer={
        <div className="space-y-2">
          <AgentPowers actions={agent.actions} />
          <AgentTraits agent={agent} />
        </div>
      }
    />
  );
}

// Super-admin browse of the AI agent catalog (ICP Analyst, Activity Digest Writer, …) and
// the actions each powers. The catalog is static, code-managed config, so this
// surface is read-only — one agent can power many actions.
function SuperAdminAgentsPage() {
  const { data: agents = [], isLoading } = useQuery(agentsOptions());

  return (
    <>
      <PageHeader
        title="Agents"
        description="The AI agents that power your actions — their models, guardrails, and what they run."
      />
      <PageContent>
        <p className="text-muted-foreground text-sm">
          Agents are code-managed — this view is read-only for now.
        </p>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading agents…</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {agents.map((agent) => (
              <AgentCard key={agent.key} agent={agent} />
            ))}
          </div>
        )}
      </PageContent>
    </>
  );
}
